import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { PaginatedApiResponse } from 'domain/Common';
import { axiosBaseQuery, getApiBaseUrl } from 'modules/api/utils';
import { UrlNames } from 'modules/app/constants';
import { configService } from 'modules/app/services';
import { getEventConfig } from 'natalie/eventConfigs';
import { getOrCreateVisitorId } from 'pages/Natalie/telemetry/visitorId';

import {
  mapAccountlessEventFromApi,
  mapNatalieEventDetailFromApi,
  mapNataliePublicEventDetailFromApi,
} from './mappers';

import type {
  AccountlessNatalieEventApiResponse,
  CreateNatalieEventApiParams,
  GetAccountlessLiveKitTokenParams,
  GetLiveKitTokenData,
  GetLiveKitTokenParams,
  GetNatalieConversationsArgs,
  GetNatalieEventListApiParams,
  NatalieConversationsPage,
  NatalieEventDetail,
  NatalieEventListItem,
  NatalieEventMetrics,
  NatalieEventPublicDetail,
  NotifyNatalieIntakeFormArgs,
  NotifyNatalieIntakeFormResponse,
  RecordAccountlessVisitArgs,
  RegisterToNatalieEventArgs,
  RequestNatalieCreateEventArgs,
  RequestNatalieCreateEventResponse,
  ScheduleAccountlessReminderArgs,
  SetNatalieInvitedCountArgs,
  UpdateNatalieEventApiParams,
} from './natalieEvent.types';
import type {
  CreateNatalieReportArgs,
  DeleteNatalieReportArgs,
  GetNatalieReportListApiParams,
  GetNatalieReportArgs,
  NatalieReport,
  NatalieReportListItem,
  RegenerateNatalieReportArgs,
  RestoreNatalieReportVersionArgs,
  RestoreNatalieReportVersionResponse,
} from './natalieReport.types';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

// natalie-api base queries (new standalone service)
const natalieApiBaseQuery = axiosBaseQuery({ baseURL: () => configService.getUrl(UrlNames.NatalieApi) });

// Backend-api base query (kept for requestAgentToLeave, not migrated to natalie-api)
const backendApiBaseQuery = axiosBaseQuery({ baseURL: getApiBaseUrl('v1') });

const ACCOUNTLESS_MAX_RETRIES = 3;

/**
 * A 4xx from the accountless API is a definitive answer — an unknown event id, or
 * an event that is not Guest-shareable — not a transient fault. RTK Query's
 * default retry condition is status-agnostic, so without this a 404 was retried
 * three times with exponential backoff, leaving the visitor on a spinner for
 * seconds before the page could respond. 5xx and network errors still retry.
 *
 * `RetryOptions` types `maxRetries` and `retryCondition` as mutually exclusive, so
 * the attempt cap is enforced here rather than alongside it. The error arrives as
 * `unknown`, hence the narrowing.
 */
export const retryUnlessClientError = (error: unknown, attempt: number): boolean => {
  const status = (error as FetchBaseQueryError | undefined)?.status;
  if (typeof status === 'number' && status >= 400 && status < 500) return false;
  return attempt <= ACCOUNTLESS_MAX_RETRIES;
};

// Same reasoning as the accountless query: a 4xx here is a definitive answer, not
// a transient fault. Since natalie-api#137 the token mint refuses a full or ended event
// with 400 JOIN_LIMIT_REACHED / EVENT_NOT_ACTIVE, and a status-agnostic retry left the
// visitor on "connecting" through three backoffs before that message could appear.
// 5xx and network errors still retry.
const natalieApiBaseQueryWithRetry = retry(natalieApiBaseQuery, {
  retryCondition: (error, _args, { attempt }) => retryUnlessClientError(error, attempt),
});

// Accountless natalie-api (separate service, no auth required for unauthenticated users)
const accountlessBaseQueryWithRetry = retry(
  axiosBaseQuery({ baseURL: () => configService.getUrl(UrlNames.AccountlessNatalieApi) }),
  { retryCondition: (error, _args, { attempt }) => retryUnlessClientError(error, attempt) }
);

export const natalieEventApi = createApi({
  reducerPath: 'natalieEventApi',
  tagTypes: [
    'NATALIE_EVENT_LIST',
    'NATALIE_EVENT_DETAILS',
    'NATALIE_EVENT_METRICS',
    'NATALIE_EVENT_CONVERSATIONS',
    'NATALIE_REPORT_LIST',
    'NATALIE_REPORT',
  ],
  baseQuery: natalieApiBaseQuery,
  endpoints: (builder) => ({
    getNatalieEventList: builder.query<PaginatedApiResponse<NatalieEventListItem>, GetNatalieEventListApiParams>({
      providesTags: ['NATALIE_EVENT_LIST'],
      query: ({ page, size, searchText, communityId, statuses }) => ({
        url: '/natalie',
        params: { page, size, searchText, communityId, statuses },
      }),
    }),
    /**
     * Reports over this community's Natalie events. Served by natalie-api rather than
     * reporting-api because only it holds the event names, so only it can search by them,
     * order chronologically and return a total count.
     */
    /**
     * One report, with the generation natalie-api is still running (`pendingGeneration`). Signed-in
     * only: the public report page never asks, so share links show no update status.
     */
    getNatalieReport: builder.query<NatalieReport, GetNatalieReportArgs>({
      providesTags: (_result, _error, { reportId }) => [{ type: 'NATALIE_REPORT', id: reportId }],
      query: ({ communityId, reportId }) => ({ url: `/communities/${communityId}/natalie/reports/${reportId}` }),
    }),
    getNatalieReportList: builder.query<PaginatedApiResponse<NatalieReportListItem>, GetNatalieReportListApiParams>({
      providesTags: ['NATALIE_REPORT_LIST'],
      query: ({ communityId, page, size, searchText }) => ({
        url: `/communities/${communityId}/natalie/reports`,
        params: { page, size, searchText },
      }),
    }),
    /**
     * Create a report over one Natalie event. Answers 201 immediately with status GENERATING —
     * insights-api builds the data asynchronously, and its upload to reporting-api is what
     * completes the report.
     *
     * Invalidates the event list as well as the report list: an event row's menu switches between
     * "Create report" and "Go to report" purely on the `reportId` that list returns, so without
     * the second tag the menu would keep offering Create until a refresh.
     */
    createNatalieReport: builder.mutation<NatalieReportListItem, CreateNatalieReportArgs>({
      query: ({ communityId, ...data }) => ({
        url: `/communities/${communityId}/natalie/reports`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['NATALIE_REPORT_LIST', 'NATALIE_EVENT_LIST'],
    }),
    deleteNatalieReport: builder.mutation<void, DeleteNatalieReportArgs>({
      query: ({ communityId, reportId }) => ({
        url: `/communities/${communityId}/natalie/reports/${reportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['NATALIE_REPORT_LIST', 'NATALIE_EVENT_LIST'],
    }),
    /**
     * Ask natalie-api for a fresh version of a report. Answers 202 with the report; insights-api
     * builds the new version asynchronously and its upload lands as a new S3 version under the same
     * reportId, so the share link and PIN are unchanged. 409 `GENERATION_IN_PROGRESS` while one is
     * already running.
     *
     * The reporting-api caches (versions, report state) live in another slice, so
     * `useRegenerateNatalieReport` invalidates those after a 202.
     */
    regenerateNatalieReport: builder.mutation<NatalieReportListItem, RegenerateNatalieReportArgs>({
      query: ({ communityId, reportId }) => ({
        url: `/communities/${communityId}/natalie/reports/${reportId}/regenerate`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        'NATALIE_REPORT_LIST',
        'NATALIE_EVENT_LIST',
        { type: 'NATALIE_REPORT', id: reportId },
      ],
    }),
    /**
     * Copy an earlier version forward as the new latest. Answers 201 once the copy has landed, so
     * unlike regenerate there is nothing to wait for. Nothing is deleted, and the share link follows
     * the new latest. 409 `GENERATION_IN_PROGRESS` while a generation is running.
     *
     * The reporting-api caches (versions, payload) live in another slice, so
     * `useRestoreNatalieReportVersion` invalidates those.
     */
    restoreNatalieReportVersion: builder.mutation<RestoreNatalieReportVersionResponse, RestoreNatalieReportVersionArgs>(
      {
        query: ({ communityId, reportId, versionId }) => ({
          url: `/communities/${communityId}/natalie/reports/${reportId}/versions/${encodeURIComponent(versionId)}/restore`,
          method: 'POST',
        }),
        invalidatesTags: (_result, _error, { reportId }) => [{ type: 'NATALIE_REPORT', id: reportId }],
      }
    ),
    createNatalieEvent: builder.mutation<NatalieEventListItem, CreateNatalieEventApiParams>({
      query: (data) => ({
        url: '/natalie',
        method: 'POST',
        data: { ...data, communityId: String(data.communityId) },
      }),
      invalidatesTags: ['NATALIE_EVENT_LIST'],
    }),
    updateNatalieEvent: builder.mutation<NatalieEventListItem, UpdateNatalieEventApiParams>({
      query: ({ id, ...data }) => ({
        url: `/natalie/${id}`,
        method: 'PUT',
        data: { ...data, communityId: String(data.communityId) },
      }),
      invalidatesTags: (_result, _error, { id }) => ['NATALIE_EVENT_LIST', { type: 'NATALIE_EVENT_DETAILS', id }],
    }),
    getPublicNatalieEvent: builder.query<NatalieEventPublicDetail, string>({
      providesTags: (_result, _error, id) => [{ type: 'NATALIE_EVENT_DETAILS', id }],
      query: (id) => ({
        url: `/natalie/${id}`,
      }),
      transformResponse: mapNataliePublicEventDetailFromApi,
    }),
    getNatalieEvent: builder.query<NatalieEventDetail, string>({
      providesTags: (_result, _error, id) => [{ type: 'NATALIE_EVENT_DETAILS', id }],
      query: (id) => ({
        url: `/natalie/${id}`,
      }),
      transformResponse: mapNatalieEventDetailFromApi,
    }),
    // Per-event conversation metrics (EP13). Owner / org-admin / community-admin only.
    getNatalieEventMetrics: builder.query<NatalieEventMetrics, string>({
      providesTags: (_result, _error, eventId) => [{ type: 'NATALIE_EVENT_METRICS', id: eventId }],
      query: (eventId) => ({
        url: `/natalie/${eventId}/metrics`,
      }),
    }),
    // One page of an event's conversations (EP12), cursor-paginated. Callers page
    // through with `nextCursor` to build the timeline at full timestamp fidelity.
    getNatalieEventConversations: builder.query<NatalieConversationsPage, GetNatalieConversationsArgs>({
      providesTags: (_result, _error, { eventId }) => [{ type: 'NATALIE_EVENT_CONVERSATIONS', id: eventId }],
      query: ({ eventId, cursor }) => ({
        url: '/natalie/conversations',
        params: { eventId, ...(cursor ? { cursor } : {}) },
      }),
    }),
    // Set the manually-entered invited count (EP14).
    setNatalieInvitedCount: builder.mutation<{ eventId: string; invitedCount: number }, SetNatalieInvitedCountArgs>({
      query: ({ eventId, invitedCount }) => ({
        url: `/natalie/${eventId}/invited-count`,
        method: 'PUT',
        data: { invitedCount },
      }),
      invalidatesTags: (_result, _error, { eventId }) => [{ type: 'NATALIE_EVENT_METRICS', id: eventId }],
    }),
    registerToNatalieEvent: builder.mutation<void, RegisterToNatalieEventArgs>({
      query: ({ id }) => ({
        url: `/natalie/${id}/register`,
        method: 'POST',
      }),
    }),
    // "Request a Natalie Event" — get-or-create the requester's own-community create_event
    // event. The server derives identity and authorises the proposed communityId, then returns
    // the create_event event id. The success effect feeds that id into the existing
    // registered-flow join chain; re-activating reuses the same event.
    requestNatalieCreateEvent: builder.mutation<RequestNatalieCreateEventResponse, RequestNatalieCreateEventArgs>({
      query: ({ communityId }) => ({
        url: '/natalie/request',
        method: 'POST',
        data: { communityId: String(communityId) },
      }),
      invalidatesTags: ['NATALIE_EVENT_LIST'],
    }),
    // Announce a submitted intake brief in the client-request channel. Called only AFTER the
    // spreadsheet row is written, because the message is a pointer to that row.
    //
    // No `invalidatesTags`: nothing cached changes. A brief is not a Natalie Session — the
    // session is created by hand off the back of the spreadsheet row, so the event list this API
    // caches has nothing new in it after a submission.
    //
    // The endpoint answers 200 whatever Slack did — a failed post must not fail the submission —
    // so the caller reads `notified` rather than the status to tell an announced submission from
    // an unannounced one.
    notifyNatalieIntakeForm: builder.mutation<NotifyNatalieIntakeFormResponse, NotifyNatalieIntakeFormArgs>({
      query: ({ communityId }) => ({
        url: '/natalie/intake-form/notify',
        method: 'POST',
        // Stringified to match the endpoint's numeric-string contract, as POST /natalie/request
        // does. The whole body: no answers, and no timestamp — Slack stamps the message and the
        // row records its own write time, so there is nothing else the channel needs.
        data: { communityId: String(communityId) },
      }),
    }),
    // requestAgentToLeave — remains on backend-api (not migrated to natalie-api)
    requestAgentToLeave: builder.mutation<void, string>({
      queryFn: async (id, api, extraOptions) => {
        const result = await backendApiBaseQuery({ url: `/natalie/${id}/leave`, method: 'POST' }, api, extraOptions);
        if (result.error) return { error: result.error };
        return { data: result.data as void };
      },
    }),
    getLiveKitToken: builder.mutation<GetLiveKitTokenData, GetLiveKitTokenParams>({
      queryFn: async ({ eventId, version, slot, mode, agentName, debug, testInviteCode }, api, extraOptions) => {
        // The token endpoint uses version + slot params to configure RoomAgentDispatch
        const params: Record<string, string> = {};
        if (version) params.version = version;
        if (slot) params.slot = slot;
        if (mode) params.mode = mode;

        // Read agent config from sessionStorage (set by Configure page)
        const sessionAgentConfig = sessionStorage.getItem('natalie-agent-config');
        let agentConfig: string | null = sessionAgentConfig;

        // Fall back to per-event config when no manual config exists
        if (!agentConfig) {
          const eventConfig = getEventConfig(eventId);
          if (eventConfig) {
            agentConfig = JSON.stringify(eventConfig);
          }
        }

        // Inject agentName into agentConfig (backend extracts it from metadata)
        if (agentName) {
          const configObj = agentConfig ? JSON.parse(agentConfig) : {};
          configObj.agentName = agentName;
          agentConfig = JSON.stringify(configObj);
        }

        // Inject debug flag into agentConfig
        if (debug) {
          const configObj = agentConfig ? JSON.parse(agentConfig) : {};
          configObj.debug = true;
          agentConfig = JSON.stringify(configObj);
        }

        const result = await natalieApiBaseQueryWithRetry(
          {
            url: '/natalie/token',
            method: 'POST',
            // testInviteCode lets the server resolve source=test and return isTest
            // for the Registered test Room (the real Registered Room omits it).
            data: {
              eventId,
              // Thread the client-minted visitor id so an authed conversation ties back
              // to its page visit in the funnel (same key path as the accountless flow).
              visitorId: getOrCreateVisitorId(),
              ...(agentConfig ? { agentConfig } : {}),
              ...(testInviteCode ? { testInviteCode } : {}),
            },
            params: Object.keys(params).length > 0 ? params : undefined,
          },
          api,
          extraOptions
        );

        // Clear sessionStorage after use (only if it was the source)
        if (sessionAgentConfig) sessionStorage.removeItem('natalie-agent-config');

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as GetLiveKitTokenData };
      },
    }),
    // Accountless: fetch event details from natalie-api (no auth)
    getAccountlessNatalieEvent: builder.query<NatalieEventPublicDetail, string>({
      queryFn: async (eventId, api, extraOptions) => {
        const result = await accountlessBaseQueryWithRetry({ url: `/natalie/${eventId}` }, api, extraOptions);
        if (result.error) return { error: result.error };
        return { data: mapAccountlessEventFromApi(result.data as AccountlessNatalieEventApiResponse) };
      },
    }),
    // Accountless: generate LiveKit token from natalie-api (no auth)
    getAccountlessLiveKitToken: builder.mutation<GetLiveKitTokenData, GetAccountlessLiveKitTokenParams>({
      queryFn: async ({ eventId, inviteCode, agentName }, api, extraOptions) => {
        const result = await accountlessBaseQueryWithRetry(
          {
            url: `/natalie/${eventId}/token/${inviteCode}`,
            method: 'POST',
            data: {
              // Thread the client-minted visitor id onto the conversation stub so the
              // visit ties to its conversation (funnel visited→started join key).
              visitorId: getOrCreateVisitorId(),
              // agentName rides in agentConfig (mirrors the authed path); the
              // server uses it as the dispatch target, else its default AGENT_NAME.
              ...(agentName ? { agentConfig: JSON.stringify({ agentName }) } : {}),
            },
          },
          api,
          extraOptions
        );
        if (result.error) return { error: result.error };
        return { data: result.data as GetLiveKitTokenData };
      },
    }),
    // Accountless: record a public invite-page visit (funnel "visited" rung, no auth).
    // Fire-and-forget analytics beacon — callers do not await/surface the result; the
    // server 202s on accept and 400s (harmlessly) for a non-joinable event.
    recordAccountlessVisit: builder.mutation<void, RecordAccountlessVisitArgs>({
      queryFn: async ({ eventId, visitorId, accountId }, api, extraOptions) => {
        const result = await accountlessBaseQueryWithRetry(
          {
            url: `/natalie/${eventId}/visit`,
            method: 'POST',
            // accountId included only for authed visitors, so the funnel can attribute
            // the visit to a known user (server stores it alongside the visitorId).
            data: { visitorId, ...(accountId ? { accountId } : {}) },
          },
          api,
          extraOptions
        );
        if (result.error) return { error: result.error };
        return { data: undefined };
      },
    }),
    // Accountless: schedule a reminder email (no auth)
    scheduleAccountlessReminder: builder.mutation<void, ScheduleAccountlessReminderArgs>({
      queryFn: async ({ eventId, inviteCode, ...body }, api, extraOptions) => {
        const result = await accountlessBaseQueryWithRetry(
          {
            url: `/natalie/${eventId}/${inviteCode}/reminder`,
            method: 'POST',
            data: body,
          },
          api,
          extraOptions
        );
        if (result.error) return { error: result.error };
        return { data: undefined };
      },
    }),
  }),
});

export const {
  useGetNatalieEventListQuery,
  useGetNatalieReportListQuery,
  useCreateNatalieReportMutation,
  useDeleteNatalieReportMutation,
  useRegenerateNatalieReportMutation,
  useRestoreNatalieReportVersionMutation,
  useGetNatalieReportQuery,
  useCreateNatalieEventMutation,
  useUpdateNatalieEventMutation,
  useGetPublicNatalieEventQuery,
  useGetNatalieEventQuery,
  useGetNatalieEventMetricsQuery,
  useLazyGetNatalieEventConversationsQuery,
  useSetNatalieInvitedCountMutation,
  useGetLiveKitTokenMutation,
  useRequestAgentToLeaveMutation,
  useRegisterToNatalieEventMutation,
  useRequestNatalieCreateEventMutation,
  useNotifyNatalieIntakeFormMutation,
  useGetAccountlessNatalieEventQuery,
  useGetAccountlessLiveKitTokenMutation,
  useRecordAccountlessVisitMutation,
  useScheduleAccountlessReminderMutation,
} = natalieEventApi;
